"use client";
import React from "react";
import roulette from "@/app/assets/roulette.webp";
import slotMachine from "@/app/assets/slot_machine.webp";
import Image from "next/image";
import Link from "next/link";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

interface Strategy {
  title: string;
  description: string;
  icon: string;
  tips: string[];
}

interface GameStrategy {
  name: string;
  description: string;
  strategies: string[];
  icon: string;
  color: string;
}

interface RiskManagement {
  title: string;
  description: string;
  icon: string;
}

interface CommonMistake {
  mistake: string;
  solution: string;
  icon: string;
}

const strategies: Strategy[] = [
  {
    title: "Bankroll Management",
    description:
      "Control your spending and bet size to extend your gaming sessions and minimize losses.",
    icon: "💰",
    tips: [
      "Set a daily or weekly budget",
      "Never bet more than you can afford to lose",
      "Use the 5% rule: bet only 5% of your bankroll per hand",
    ],
  },
  {
    title: "Understanding House Edge",
    description:
      "Know the mathematical advantage the casino has in each game to make informed choices.",
    icon: "📊",
    tips: [
      "Choose games with lower house edges",
      "Blackjack has ~0.5% house edge",
      "Slots typically have 2-15% house edge",
    ],
  },
  {
    title: "Optimal Betting Strategy",
    description:
      "Use proven betting patterns to balance risk and reward effectively.",
    icon: "🎯",
    tips: [
      "Start with small bets and increase gradually",
      "Adjust bets based on winning streaks",
      "Never chase losses with larger bets",
    ],
  },
  {
    title: "Game Selection",
    description:
      "Pick games where skill and strategy can influence outcomes more significantly.",
    icon: "🎮",
    tips: [
      "Blackjack rewards skill and strategy",
      "Poker allows player decisions",
      "Avoid purely luck-based games",
    ],
  },
];

const gameStrategies: GameStrategy[] = [
  {
    name: "Blackjack Strategy",
    description:
      "Master basic strategy and card counting techniques for blackjack.",
    icon: "🂡",
    color: "from-red-500 to-red-600",
    strategies: [
      "Always split Aces and 8s",
      "Never split 10s or 5s",
      "Hit on 16 or less against dealer 7+",
      "Use basic strategy charts for optimal plays",
      "Keep a running count of high/low cards",
    ],
  },
  {
    name: "Roulette Strategy",
    description:
      "Learn betting patterns and probability-based approaches for roulette.",
    icon: "🎡",
    color: "from-yellow-500 to-yellow-600",
    strategies: [
      "Understand European vs American roulette (different house edges)",
      "Use Martingale strategy cautiously",
      "Bet on multiple numbers to increase chances",
      "Avoid betting on single numbers repeatedly",
      "Combine inside and outside bets wisely",
    ],
  },
  {
    name: "Poker Strategy",
    description:
      "Develop winning poker tactics through position, psychology, and ranges.",
    icon: "🃏",
    color: "from-green-500 to-green-600",
    strategies: [
      "Play tighter from early positions",
      "Understand hand rankings and probabilities",
      "Master pot odds calculations",
      "Read opponents and their betting patterns",
      "Manage your emotions and avoid tilt",
    ],
  },
  {
    name: "Slot Machine Strategy",
    description:
      "Maximize slot winnings through smart game selection and play patterns.",
    icon: "🎰",
    color: "from-blue-500 to-blue-600",
    strategies: [
      "Choose slots with higher RTP (Return to Player)",
      "Understand volatility: low vs high variance",
      "Play maximum coins on progressive slots",
      "Set win/loss limits before playing",
      "Use free spins and bonuses effectively",
    ],
  },
];

const riskManagement: RiskManagement[] = [
  {
    title: "Set Loss Limits",
    description:
      "Determine the maximum amount you're willing to lose in a session.",
    icon: "❌",
  },
  {
    title: "Set Win Targets",
    description: "Decide when to stop playing after reaching your profit goal.",
    icon: "✅",
  },
  {
    title: "Take Regular Breaks",
    description: "Step away from the game every 30-60 minutes to stay sharp.",
    icon: "⏸️",
  },
  {
    title: "Never Borrow to Gamble",
    description: "Only use money you already have and can afford to lose.",
    icon: "🚫",
  },
];

const commonMistakes: CommonMistake[] = [
  {
    mistake: "Chasing Losses",
    solution:
      "Accept losses as part of gambling. Increase bets trying to recover losses leads to bigger losses.",
    icon: "🔄",
  },
  {
    mistake: "Playing While Emotional",
    solution:
      "Avoid gambling when angry, sad, or stressed. Emotions cloud judgment and lead to poor decisions.",
    icon: "😤",
  },
  {
    mistake: "Ignoring Bankroll Management",
    solution:
      "Follow strict bankroll rules. This is the foundation of sustainable gambling.",
    icon: "📉",
  },
  {
    mistake: "Believing in Hot/Cold Streaks",
    solution:
      "Each game outcome is independent. Past results don't influence future outcomes.",
    icon: "🔮",
  },
];

const CasinoStrategiesClient = () => {
  return (
    <div className="m-0 w-full flex flex-col items-center text-white bg-[#141414]">
      <Navbar />
      <main className="w-full flex flex-col items-center justify-start pt-24">
        {/* Hero Section */}
        <section className="w-full px-4 py-12 md:py-20 border-b border-white/10">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-6 tracking-tight drop-shadow-lg bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">
              Casino Winning Strategies 2025
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Master proven strategies for blackjack, roulette, poker, and
              slots. Learn expert tips to increase your winning odds and play
              smarter.
            </p>
            <Link
              href="/casinos"
              className="inline-block px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg font-semibold hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300 hover:scale-105"
            >
              Play Now at Casino24
            </Link>
          </div>
        </section>

        {/* Core Strategies Section */}
        <section className="w-full px-4 py-16 md:py-24 bg-gradient-to-b from-gray-900 via-gray-800/50 to-gray-900">
          <div className="max-w-7xl mx-auto">
            <div className="mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
                Core Casino Strategies
              </h2>
              <p className="text-center text-gray-400 max-w-2xl mx-auto text-sm md:text-base">
                Foundation principles that apply to all casino games
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {strategies.map((strategy, idx) => (
                <div
                  key={idx}
                  className="group relative h-full overflow-hidden rounded-xl md:rounded-2xl bg-gradient-to-br from-blue-950/40 via-[#1e1e2e] to-[#0f1020] border border-blue-500/20 p-6 md:p-8 hover:border-blue-500/60 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/30 transform hover:-translate-y-1"
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-blue-500/15 via-transparent to-purple-500/15" />
                  <div className="absolute top-0 left-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl -top-16 -left-16 group-hover:scale-150 transition-transform duration-500" />

                  <div className="relative z-10">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 mb-4 group-hover:scale-110 transition-transform duration-300">
                      <span className="text-4xl">{strategy.icon}</span>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold mb-3 group-hover:text-blue-300 transition-colors duration-300 line-clamp-2">
                      {strategy.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed group-hover:text-gray-300 transition-colors duration-300 mb-4">
                      {strategy.description}
                    </p>
                    <ul className="text-xs text-gray-500 space-y-1">
                      {strategy.tips.map((tip, tipIdx) => (
                        <li key={tipIdx} className="flex items-start gap-2">
                          <span className="text-blue-400 flex-shrink-0">•</span>
                          <span className="group-hover:text-gray-400 transition-colors">
                            {tip}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 w-0 h-1 bg-gradient-to-r from-blue-500 to-purple-500 group-hover:w-full transition-all duration-500" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Game Strategies Section */}
        <section className="w-full px-4 py-16 md:py-24 bg-gradient-to-b from-transparent via-purple-900/10 to-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-center bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">
                Specific Game Strategies
              </h2>
              <p className="text-center text-gray-400 max-w-2xl mx-auto text-sm md:text-base">
                Detailed tactics for each major casino game
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {gameStrategies.map((game, idx) => (
                <div
                  key={idx}
                  className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a1f2e] to-[#0d1117] border border-purple-500/20 p-6 md:p-8 hover:border-purple-500/60 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/30"
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-purple-500/10 via-transparent to-pink-500/10" />
                  <div className="absolute top-0 right-0 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl -top-24 -right-24 group-hover:scale-125 transition-transform duration-500" />

                  <div className="relative z-10">
                    <div className="flex items-start gap-4 mb-4">
                      <span className="text-5xl flex-shrink-0">
                        {game.icon}
                      </span>
                      <div>
                        <h3 className="text-2xl font-bold group-hover:text-purple-300 transition-colors">
                          {game.name}
                        </h3>
                        <p className="text-gray-400 text-sm mt-1">
                          {game.description}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {game.strategies.map((strategy, stratIdx) => (
                        <li
                          key={stratIdx}
                          className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-purple-500/20 group-hover:border-purple-500/40 group-hover:bg-white/10 transition-all"
                        >
                          <span className="text-purple-400 font-bold flex-shrink-0">
                            →
                          </span>
                          <span className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors">
                            {strategy}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Image - Roulette */}
        <section className="w-full px-4 py-16 md:py-24">
          <div className="max-w-xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl">
              <Image
                src={roulette}
                alt="Roulette strategy - Casino wheel and betting strategies"
                className="w-full h-auto object-cover"
                priority
              />
            </div>
          </div>
        </section>

        {/* Risk Management Section */}
        <section className="w-full px-4 py-16 md:py-24 bg-gradient-to-b from-transparent via-green-900/5 to-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="mb-14">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-center bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-400">
                Risk Management Essentials
              </h2>
              <p className="text-center text-gray-400 max-w-2xl mx-auto text-sm md:text-base">
                Protect your bankroll with proven risk management techniques
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              {riskManagement.map((risk, idx) => (
                <div
                  key={idx}
                  className="group relative overflow-hidden rounded-xl md:rounded-2xl bg-gradient-to-br from-green-950/30 via-[#1e1e2e] to-[#0f1020] border border-green-500/20 p-6 md:p-8 hover:border-green-500/60 transition-all duration-500 hover:shadow-lg hover:shadow-green-500/25 transform hover:-translate-y-1"
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-green-500/10 via-transparent to-emerald-500/10" />
                  <div className="absolute top-0 left-0 w-32 h-32 bg-green-500/10 rounded-full blur-3xl -top-16 -left-16 group-hover:scale-150 transition-transform duration-500" />

                  <div className="relative z-10">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 mt-1 group-hover:scale-125 transition-transform duration-300" />
                      <h3 className="text-lg md:text-xl font-bold group-hover:text-green-300 transition-colors duration-300">
                        {risk.title}
                      </h3>
                    </div>
                    <p className="text-gray-400 text-sm md:text-base leading-relaxed group-hover:text-gray-300 transition-colors duration-300 ml-8">
                      {risk.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Image - Slots */}
        <section className="w-full px-4 py-8">
          <div className="max-w-xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl shadow-2xl">
              <Image
                src={slotMachine}
                alt="Slot machine strategies - RTP and volatility tips"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* Common Mistakes Section */}
        <section className="w-full px-4 py-16 md:py-24 bg-gradient-to-b from-transparent via-red-900/5 to-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="mb-14">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-center bg-clip-text text-transparent bg-gradient-to-r from-red-400 to-pink-400">
                Mistakes to Avoid
              </h2>
              <p className="text-center text-gray-400 max-w-2xl mx-auto text-sm md:text-base">
                Common pitfalls that lead to losses and how to prevent them
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {commonMistakes.map((item, idx) => (
                <div
                  key={idx}
                  className="group relative overflow-hidden rounded-xl md:rounded-2xl bg-gradient-to-br from-red-950/30 via-[#1a1f2e] to-[#0d1117] border border-red-500/20 p-6 md:p-8 hover:border-red-500/60 transition-all duration-500 hover:shadow-2xl hover:shadow-red-500/25"
                >
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-red-500/10 via-transparent to-pink-500/10" />
                  <div className="absolute top-0 right-0 w-40 h-40 bg-red-500/10 rounded-full blur-3xl -top-20 -right-20 group-hover:scale-150 transition-transform duration-500" />

                  <div className="relative z-10">
                    <div className="flex items-start gap-4 mb-4">
                      <span className="text-4xl flex-shrink-0">
                        {item.icon}
                      </span>
                      <h3 className="text-lg md:text-xl font-bold group-hover:text-red-300 transition-colors">
                        {item.mistake}
                      </h3>
                    </div>
                    <p className="text-gray-400 text-sm md:text-base leading-relaxed group-hover:text-gray-300 transition-colors">
                      <span className="font-semibold text-green-300">
                        Solution:
                      </span>{" "}
                      {item.solution}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Responsible Gambling Section */}
        <section className="w-full px-4 py-16 md:py-24 bg-gradient-to-b from-transparent via-orange-900/10 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-orange-900/30 to-yellow-900/20 border border-orange-500/30 p-8 md:p-12">
              <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl -top-32 -right-32" />

              <div className="relative z-10 flex flex-col md:flex-row gap-8 items-start md:items-center">
                <div className="text-6xl flex-shrink-0">⚖️</div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-4 text-orange-300">
                    Responsible Gambling First
                  </h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Strategies increase odds, but gambling always carries risk.
                    Never gamble with money you can't afford to lose. Casino
                    games are designed for entertainment, not income. Set strict
                    limits, take breaks, and seek help if gambling becomes
                    problematic.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <span className="px-4 py-2 bg-orange-500/20 rounded-lg text-sm text-orange-200 border border-orange-500/30">
                      Set limits
                    </span>
                    <span className="px-4 py-2 bg-orange-500/20 rounded-lg text-sm text-orange-200 border border-orange-500/30">
                      Take breaks
                    </span>
                    <span className="px-4 py-2 bg-orange-500/20 rounded-lg text-sm text-orange-200 border border-orange-500/30">
                      Play for fun
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full px-4 py-16 md:py-24 bg-gradient-to-b from-transparent to-blue-900/20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="rounded-3xl bg-gradient-to-r from-blue-600/30 to-purple-600/30 border border-blue-500/50 p-8 md:p-12 backdrop-blur">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Apply These Strategies?
              </h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
                Join CasinoB8 and put these proven strategies into practice.
                Play responsibly and enjoy the thrill of online casino gaming
                with expert knowledge.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href="/"
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all duration-300 hover:scale-105"
                >
                  Back to CasinoB8
                </Link>
                <Link
                  href="/mobile-casinos"
                  className="px-8 py-3 border border-blue-500/50 text-blue-300 rounded-lg font-semibold hover:bg-blue-500/10 transition-all duration-300"
                >
                  Play on Mobile
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CasinoStrategiesClient;
